import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function ServicesPage() {
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${import.meta.env.VITE_SITE_URL}/services`,
      "url": `${import.meta.env.VITE_SITE_URL}/services`,
      "name": "Services Télécom & Énergie Belgique - Orange, Eneco, Entretien Chaudière | JLD Concept",
      "description": "Forfaits GSM, Internet haut débit, TV premium avec Orange Belgique. Énergie verte 100% renouvelable avec Eneco. Entretien chaudière par techniciens certifiés. Économisez dès aujourd'hui.",
      "inLanguage": "fr-BE",
      "isPartOf": { "@id": `${import.meta.env.VITE_SITE_URL}/#website` },
      "mainEntity": {
        "@type": "ItemList",
        "name": "Services JLD Concept",
        "itemListElement": [
          {
            "@type": "Service",
            "position": 1,
            "name": "Solutions Télécom Orange Belgique",
            "description": "Forfaits GSM, Internet haut débit jusqu'à 1GB et TV premium avec plus de 70 chaînes.",
            "provider": { "@type": "Organization", "name": "JLD Concept SRL" },
            "areaServed": "BE"
          },
          {
            "@type": "Service",
            "position": 2,
            "name": "Énergie Verte Eneco",
            "description": "Électricité et gaz 100% renouvelables avec tarifs compétitifs garantis.",
            "provider": { "@type": "Organization", "name": "JLD Concept SRL" },
            "areaServed": "BE"
          },
          {
            "@type": "Service",
            "position": 3,
            "name": "Entretien Chaudière",
            "description": "Service professionnel d'entretien annuel par techniciens certifiés, intervention rapide.",
            "provider": { "@type": "Organization", "name": "JLD Concept SRL" },
            "areaServed": "BE"
          }
        ]
      }
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-services';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById('schema-services')?.remove(); };
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />

      <div className="pt-24">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-orange-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-block text-sm font-semibold text-orange-600 bg-orange-100 px-4 py-2 rounded-full mb-4">
                  NOS SERVICES
                </div>
                <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                  Solutions intégrées pour votre quotidien
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Découvrez notre gamme complète de services en télécom et énergie, conçus pour 
                  vous faire économiser tout en garantissant une qualité exceptionnelle.
                </p>
              </div>

              <div
                className="w-full h-96 bg-cover bg-center rounded-2xl shadow-xl"
                style={{
                  backgroundImage: 'url(https://public.readdy.ai/ai/img_res/93806b449b0fd619847fa987ea18ed9b.jpg)',
                }}
              ></div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Telecom Service */}
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 text-white hover:scale-105 transition-transform">
                <div className="mb-6">
                  <h3 className="text-3xl font-bold mb-4">Solutions Télécom</h3>
                  <p className="text-gray-300 mb-6">
                    Forfaits GSM, Internet et TV avec Orange Belgique
                  </p>
                </div>

                <div
                  className="w-full h-48 bg-cover bg-center rounded-xl mb-6"
                  style={{
                    backgroundImage: 'url(https://readdy.ai/api/search-image?query=Modern%20telecommunications%20equipment%20with%20smartphones%20and%20fiber%20optic%20technology%2C%20professional%20telecom%20devices%20on%20clean%20background%2C%20high%20tech%20mobile%20and%20internet%20connectivity%20setup&width=600&height=400&seq=telecom001&orientation=landscape)',
                  }}
                ></div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <i className="ri-smartphone-line text-orange-400 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-semibold mb-1">Forfaits GSM</h4>
                      <p className="text-sm text-gray-300">Appels, SMS et data illimités avec le réseau Orange</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="ri-wifi-line text-orange-400 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-semibold mb-1">Internet Haut Débit</h4>
                      <p className="text-sm text-gray-300">Jusqu'à 1GB de vitesse pour toute la famille</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="ri-tv-line text-orange-400 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-semibold mb-1">TV Premium</h4>
                      <p className="text-sm text-gray-300">Plus de 70 chaînes et plusieurs bouquets au choix</p>
                    </div>
                  </div>
                </div>

                <Link
                  to="/contact"
                  className="block w-full text-center px-6 py-3 bg-orange-600 hover:bg-orange-700 rounded-full font-semibold transition-colors whitespace-nowrap"
                >
                  En savoir plus
                </Link>
              </div>

              {/* Energy Service */}
              <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-8 shadow-lg hover:scale-105 transition-transform">
                <div className="flex items-center space-x-2 bg-green-100 px-3 py-1.5 rounded-full w-fit mb-6">
                  <i className="ri-flashlight-line text-green-600"></i>
                  <span className="text-sm font-semibold text-green-700">ÉNERGIE</span>
                </div>

                <div className="relative w-full h-48 rounded-xl mb-6 overflow-hidden">
                  <div
                    className="w-full h-full bg-cover bg-center"
                    style={{
                      backgroundImage: 'url(https://readdy.ai/api/search-image?query=Green%20renewable%20energy%20with%20solar%20panels%20and%20wind%20turbines%20in%20sunny%20landscape%2C%20sustainable%20clean%20power%20generation%20facilities%2C%20bright%20natural%20environment%20with%20blue%20sky&width=600&height=400&seq=energy001&orientation=landscape)',
                    }}
                  ></div>
                  <div className="absolute top-3 right-3 bg-white/20 backdrop-blur-sm rounded-lg p-1.5">
                    <img
                      src="https://static.readdy.ai/image/9c58d8b9dc921ceebc49b67abc2b926b/e670cdcaf1ecd738ddf0cd16b324bd0d.png"
                      alt="Eneco"
                      className="h-8 w-auto object-contain"
                    />
                  </div>
                </div>

                <h3 className="text-3xl font-bold text-gray-900 mb-4">Énergie Verte</h3>
                <p className="text-gray-600 mb-6">
                  Votre énergie 100% verte avec notre partenaire Eneco
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-2">
                    <i className="ri-leaf-line text-green-600"></i>
                    <span className="text-sm text-gray-700">100% énergie renouvelable</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <i className="ri-price-tag-3-line text-green-600"></i>
                    <span className="text-sm text-gray-700">Tarifs compétitifs garantis</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <i className="ri-customer-service-line text-green-600"></i>
                    <span className="text-sm text-gray-700">Service client dédié</span>
                  </div>
                </div>

                <Link
                  to="/contact"
                  className="block w-full text-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-full font-semibold transition-colors whitespace-nowrap"
                >
                  En savoir plus
                </Link>
              </div>

              {/* Maintenance Service */}
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg hover:scale-105 transition-transform">
                <div className="flex items-center space-x-2 bg-blue-100 px-3 py-1.5 rounded-full w-fit mb-6">
                  <i className="ri-tools-line text-blue-600"></i>
                  <span className="text-sm font-semibold text-blue-700">MAINTENANCE</span>
                </div>

                <div
                  className="w-full h-48 bg-cover bg-center rounded-xl mb-6"
                  style={{
                    backgroundImage: 'url(https://readdy.ai/api/search-image?query=Professional%20heating%20technician%20servicing%20modern%20boiler%20system%20in%20clean%20home%20environment%2C%20maintenance%20expert%20with%20tools%20working%20on%20heating%20equipment%2C%20bright%20indoor%20setting&width=600&height=400&seq=maintenance001&orientation=landscape)',
                  }}
                ></div>

                <h3 className="text-3xl font-bold text-gray-900 mb-4">Entretien Chaudière</h3>
                <p className="text-gray-600 mb-6">
                  Service professionnel d'entretien et de maintenance
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-2">
                    <i className="ri-calendar-check-line text-blue-600"></i>
                    <span className="text-sm text-gray-700">Entretien annuel obligatoire</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <i className="ri-shield-check-line text-blue-600"></i>
                    <span className="text-sm text-gray-700">Techniciens certifiés</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <i className="ri-time-line text-blue-600"></i>
                    <span className="text-sm text-gray-700">Intervention rapide</span>
                  </div>
                </div>

                <Link
                  to="/contact"
                  className="block w-full text-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full font-semibold transition-colors whitespace-nowrap"
                >
                  En savoir plus
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Pourquoi choisir nos services?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Des avantages concrets pour améliorer votre quotidien
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-orange-100 rounded-full mx-auto mb-4">
                  <i className="ri-money-euro-circle-line text-4xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Économies garanties</h3>
                <p className="text-gray-600 text-sm">
                  Réduisez vos factures mensuelles sans compromis sur la qualité
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                  <i className="ri-shield-star-line text-4xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Qualité premium</h3>
                <p className="text-gray-600 text-sm">
                  Partenaires reconnus : Orange Belgique et Eneco
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-4">
                  <i className="ri-customer-service-2-line text-4xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Support dédié</h3>
                <p className="text-gray-600 text-sm">
                  Un consultant personnel à votre écoute
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-purple-100 rounded-full mx-auto mb-4">
                  <i className="ri-speed-up-line text-4xl text-purple-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Installation rapide</h3>
                <p className="text-gray-600 text-sm">
                  Mise en service sous 48h pour la plupart des services
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-orange-600 to-orange-500">
          <div className="max-w-4xl mx-auto px-6 text-center text-white">
            <h2 className="text-4xl font-bold mb-6">
              Prêt à économiser sur vos services?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Contactez-nous dès aujourd'hui pour une offre personnalisée
            </p>
            <Link
              to="/contact"
              className="inline-block px-8 py-4 bg-white text-orange-600 font-semibold rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Demander un devis gratuit
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}